<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-1">
      <h2 class="home-section-title mb-0">
        Tus contratos
      </h2>
      <b-button
        variant="flat-secondary"
        size="sm"
        @click="search"
      >
        <feather-icon icon="RefreshCwIcon" />
        Actualizar
      </b-button>
    </div>

    <h5
      v-if="!isBusy && items.length > 1 && total > 0"
      class="mb-2"
    >
      Total pendiente:
      <span class="text-danger font-weight-bolder">RD$ {{ total }}</span>
    </h5>

    <div v-if="isBusy">
      <div
        v-for="n in 2"
        :key="n"
        class="skeleton mb-1"
        style="height: 104px"
      />
    </div>

    <div
      v-else-if="items.length === 0"
      class="text-center py-4"
    >
      <div class="contract-empty-icon mx-auto mb-1">
        <feather-icon
          icon="FileTextIcon"
          size="24"
        />
      </div>
      <p class="mb-0">
        No hay contratos asociados a tu cuenta
      </p>
      <p class="text-muted small">
        Si crees que esto es un error, contáctanos al
        {{ business.phone }}
      </p>
    </div>

    <div v-else>
      <b-card
        v-for="item in items"
        :key="item.idcontrato"
        no-body
        class="mb-1 home-contract-card"
      >
        <div class="home-contract-panel">
          <!-- Identidad del contrato -->
          <div class="home-contract-main">
            <b-avatar
              :text="avatarText(item.apenom)"
              variant="light-primary"
              size="48"
            />
            <div class="home-contract-identity">
              <div class="home-contract-heading">
                <h6 class="home-contract-code mb-0">
                  {{ item.codigo }}
                </h6>
                <b-badge
                  pill
                  :variant="item.class"
                >
                  {{ item.estado }}
                </b-badge>
              </div>
              <p class="home-contract-name mb-0">
                {{ item.apenom }}
              </p>

              <div class="home-contract-meta">
                <div
                  v-if="item.direccion"
                  class="home-contract-meta-item"
                >
                  <feather-icon
                    icon="MapPinIcon"
                    size="14"
                  />
                  <span>{{ item.direccion }}</span>
                </div>
                <!-- El NUI es el único dato con etiqueta: un número suelto al
                     lado de un teléfono y un correo no se identifica solo. Es
                     la misma etiqueta que usa el resumen del contrato. -->
                <div
                  v-if="item.ruc"
                  class="home-contract-meta-item"
                >
                  <feather-icon
                    icon="HashIcon"
                    size="14"
                  />
                  <span>NUI {{ item.ruc }}</span>
                </div>
                <div
                  v-if="item.telefono"
                  class="home-contract-meta-item"
                >
                  <feather-icon
                    icon="PhoneIcon"
                    size="14"
                  />
                  <span>{{ item.telefono }}</span>
                </div>
                <div
                  v-if="item.email"
                  class="home-contract-meta-item"
                >
                  <feather-icon
                    icon="MailIcon"
                    size="14"
                  />
                  <span>{{ item.email }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Bloque de pago -->
          <div class="home-contract-pay">
            <p class="home-contract-pay-label mb-0">
              Balance pendiente
            </p>
            <h3 class="home-contract-balance mb-0">
              RD$ {{ item.balanceContract }}
            </h3>

            <div
              v-if="item.balance_pesos > 0"
              class="home-contract-actions"
            >
              <b-button
                v-b-modal.modal-checkout
                block
                variant="danger"
                class="home-pay-btn"
                @click="pagar(item)"
              >
                Pagar RD$ {{ item.balance_pesos }}
              </b-button>
              <b-button
                block
                variant="outline-secondary"
                @click="resumenContract(item.idcontrato)"
              >
                Ver resumen
              </b-button>
            </div>

            <div
              v-else
              class="home-contract-actions"
            >
              <div class="home-contract-settled">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="15"
                />
                Cuenta al día
              </div>
              <b-button
                block
                variant="outline-secondary"
                @click="resumenContract(item.idcontrato)"
              >
                Ver resumen
              </b-button>
            </div>
          </div>
        </div>
      </b-card>
    </div>

    <b-modal
      id="modal-checkout"
      ref="modal-checkout"
      content-class="shadow"
      ok-only
      centered
      ok-variant="outline-secondary"
      ok-title="Cerrar"
      no-close-on-backdrop
      size="lg"
      title="Pago"
    >
      <checkout
        :contract="contract"
        :idcontrato="contract.idcontrato"
      />
    </b-modal>
  </div>
</template>

<script>
import { avatarText } from "@core/utils/filter"
import { BAvatar, BBadge, BButton, BCard } from "bootstrap-vue"
import business from "@/business"
import Checkout from "./Checkout.vue"
import useList from "./useList"

export default {
  components: {
    BCard,
    BAvatar,
    BBadge,
    BButton,
    Checkout,
  },
  setup() {
    const {
      perPage,
      currentPage,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
    } = useList()

    return {
      perPage,
      currentPage,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refUserListTable,
      refetchData,
    }
  },
  data() {
    return {
      isBusy: false,
      userData: JSON.parse(localStorage.getItem("userData")),
      avatarText,
      business,
      items: [],
      contract: {
        idcontrato: null,
      },
    }
  },
  computed: {
    total() {
      let suma = 0
      for (let i = 0; i < this.items.length; i++) {
        suma += parseFloat(this.items[i].balance_pesos)
      }

      return suma.toFixed(2)
    },
  },
  created() {
    this.search()
  },
  methods: {
    search() {
      this.isBusy = true
      this.$http
        .get("/usuario-contrato", {
          params: {
            idusuario: this.userData.id,
            estado: "A",
          },
        })
        .then((response) => {
          this.isBusy = false
          this.items = response.data.data || []
        })
        .catch((error) => {
          console.log(error)
          this.isBusy = false
          this.$swal({
            title: "Error",
            text: error.response.data.error,
            icon: "error",
            customClass: {
              confirmButton: "btn btn-danger",
            },
            buttonsStyling: false,
          })
        })
    },
    resumenContract(idcontrato) {
      this.$router.push({
        name: "contract-id",
        params: { id: idcontrato },
      })
    },
    pagar(item) {
      this.contract = item
    },
  },
}
</script>

<style lang="scss" scoped>
.home-section-title {
    font-size: 0.9375rem;
    font-weight: 600;
}

.contract-empty-icon {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--tm-radius-pill);
    background-color: rgba(var(--tm-primary-rgb), var(--tm-tint));
    color: var(--tm-primary);
}

// La tarjeta ocupa todo el ancho y se parte en dos zonas: identidad a la
// izquierda y el bloque de pago a la derecha, con fondo y borde propios. Es la
// pieza más importante del portal, y a lo ancho una sola fila de datos se leía
// como fila de tabla; separar el pago en su propia columna le da al ancho un
// motivo y deja el monto pegado a su CTA.
//
// `overflow: hidden` es lo que recorta el fondo del aside contra el radio de la
// tarjeta; sin eso asoman las esquinas cuadradas.
.home-contract-card {
    overflow: hidden;
}

.home-contract-panel {
    display: flex;
    align-items: stretch;
}

.home-contract-main {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.5rem;
}

.home-contract-identity {
    min-width: 0;
}

// El código y el estado comparten línea: con el bloque de pago a la derecha, el
// badge se queda sin su esquina.
.home-contract-heading {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.home-contract-code {
    font-size: 1rem;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
}

.home-contract-name {
    font-size: 0.9375rem;
    color: var(--tm-text-secondary);
}

// Dirección, NUI, teléfono y correo (los cuatro vienen del endpoint) en dos
// columnas: la identidad sola no llenaba su columna y el bloque quedaba corto al
// lado del aside. Cuatro datos en 2×2 llenan la caja sin dejar huecos y le dan
// al lado izquierdo un alto parecido al del bloque de pago.
.home-contract-meta {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.35rem 1.5rem;
    margin-top: 0.75rem;
}

.home-contract-meta-item {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    min-width: 0;
    font-size: 0.8125rem;
    color: var(--tm-text-muted);

    svg {
        flex-shrink: 0;
    }

    span {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
}

.home-contract-pay {
    flex: 0 0 320px;
    display: flex;
    flex-direction: column;
    padding: 1.5rem;
    background-color: var(--tm-surface-muted);
    border-left: 1px solid var(--tm-border);
}

.home-contract-pay-label {
    font-size: 0.8125rem;
    color: var(--tm-text-muted);
}

.home-contract-balance {
    font-size: 1.75rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
}

.home-contract-actions {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1.25rem;

    // `block` en b-button ya trae su propio margen inferior; el gap lo cubre.
    .btn + .btn {
        margin-top: 0;
    }
}

// Sin saldo no hay CTA de pago: el estado ocupa su lugar como chip, no como
// botón deshabilitado (un botón apagado se lee como "algo que falló").
.home-contract-settled {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.4rem;
    padding: 0.6rem 0.75rem;
    border-radius: var(--tm-radius-sm);
    background-color: rgba(var(--tm-success-rgb), var(--tm-tint));
    color: var(--tm-success);
    font-size: 0.875rem;
    font-weight: 600;
}

.home-pay-btn {
    box-shadow: 0 4px 10px rgba(var(--tm-danger-rgb), 0.28);
}

// En angosto el aside pasa abajo, a ancho completo, y el borde se mueve arriba.
@media (max-width: 767.98px) {
    .home-contract-panel {
        flex-direction: column;
    }

    .home-contract-main {
        align-items: flex-start;
    }

    .home-contract-meta {
        grid-template-columns: minmax(0, 1fr);
    }

    .home-contract-pay {
        flex: 1 1 auto;
        border-left: 0;
        border-top: 1px solid var(--tm-border);
    }
}
</style>
